// HTTP API shapes shared by server and client (spec §7.3).
import type { BillingStatus, PlanId, PlanMode } from './billing.ts';

export type WorkspaceRole = 'owner' | 'admin' | 'member' | 'guest';
export type BoardRole = 'editor' | 'viewer';
export type Visibility = 'workspace' | 'private';

export const WORKSPACE_ROLES: readonly WorkspaceRole[] = ['owner', 'admin', 'member', 'guest'];
export const INVITABLE_ROLES: readonly WorkspaceRole[] = ['admin', 'member', 'guest'];

export interface UserInfo { id: string; email: string; name: string; avatarUrl: string | null }

export interface WorkspaceInfo {
  id: string; name: string; slug: string; role: WorkspaceRole;
  plan: PlanId; billingStatus: BillingStatus; trialEndsAt: number | null;
}

/** A workspace's plan, for the Plan page and the banners (spec §14.1). */
export interface BillingInfo {
  plan: PlanId;
  status: BillingStatus;
  mode: PlanMode;
  trialEndsAt: number | null;
  cancelAt: number | null;     // a scheduled cancellation takes effect then
  seats: number;               // non-guest members, what Team bills for
  canManage: boolean;          // the viewer is an owner
  trialAvailable: boolean;     // inviting a member would start the viewer's trial
  billingReady: boolean;       // Stripe is set up on this server
  hasSubscription: boolean;    // the billing portal is available
  storageUsed: number;         // bytes of images and audio on the workspace's boards (spec §19.6)
  storageLimit: number | null; // null: no limit (Team)
}

export interface MeResponse { user: UserInfo; workspaces: WorkspaceInfo[] }

export interface MemberInfo { user: UserInfo; role: WorkspaceRole; joinedAt: number }

export interface InviteInfo { id: string; email: string; role: WorkspaceRole; invitedBy: string; expiresAt: number }

export interface MembersResponse { members: MemberInfo[]; invites: InviteInfo[]; canManage: boolean }

export interface BoardInfo {
  id: string;
  workspaceId: string;
  title: string;
  visibility: Visibility;
  createdBy: string;
  createdAt: number;
  updatedAt: number;
  canEdit: boolean;
  canManage: boolean;
  /** Published to web (spec §17): the public link's token, else null. */
  publicToken: string | null;
  publicIndexable: boolean;
  canPublish: boolean;
  canUnpublish: boolean;
  /** When its thumbnail was last saved (spec §17.3), for cache-busting; null: none yet. */
  thumbAt: number | null;
}

/** How long a deleted board stays in the trash (spec §16.4). */
export const TRASH_DAYS = 30;

/** A board in the trash, as the trash page lists it. */
export interface TrashedBoard { id: string; title: string; deletedAt: number; deletedBy: string | null; purgeAt: number }

export interface BoardAccessEntry { userId: string; role: BoardRole }

/** Someone who can see a board: who cards can be assigned to (spec §13.3). */
export interface Person { id: string; name: string }

/** Board metadata; the content itself arrives over the WebSocket (spec §6). */
export interface BoardResponse {
  board: BoardInfo;
  workspace: WorkspaceInfo;
}

export interface InvitePreview { workspaceName: string; inviterName: string; email: string; role: WorkspaceRole; valid: boolean }

export interface ApiError { error: string }

export interface VersionInfo {
  seq: number;
  createdAt: number;
  opCount: number;
  authors: { userId: string; name: string; ops: number }[];
}

/** Image variants (spec §5.7): longest side ≤ size. The client uploads all three. */
export const IMAGE_VARIANTS = [
  { name: '256', size: 256 },
  { name: '1024', size: 1024 },
  { name: 'original', size: 4096 },
] as const;
export type ImageVariant = (typeof IMAGE_VARIANTS)[number]['name'];
export const IMAGE_UPLOAD_TYPES: readonly string[] = ['image/webp', 'image/png', 'image/jpeg'];

/** A device signed in to the account (spec §16.2). Session ids never leave the server. */
export interface SessionInfo { signedInAt: number; lastSeenAt: number; browser: string; os: string; current: boolean }

/** What deleting your account would do, or why it can't be done yet (spec §16.2). */
export interface DeletionPreview {
  blocked: string | null;
  deletes: { name: string; boards: number }[];
  handsOver: { name: string; to: string }[];
}

/** Publishing details for the Share dialog (spec §17.1). */
export interface PublishInfo { token: string | null; indexable: boolean; views30: number; blocked: boolean }

/** What a public page needs to know about its board (spec §17.2). */
export interface PublicBoardInfo { title: string; updatedAt: number; badge: boolean; thumbnail: boolean }

/** Why someone reports a public board (spec §17.4). */
export const REPORT_REASONS = ['illegal', 'harassment', 'spam', 'sexual', 'copyright', 'other'] as const;
export type ReportReason = (typeof REPORT_REASONS)[number];
export const REPORT_LABELS: Record<ReportReason, string> = {
  illegal: 'Illegal content', harassment: 'Harassment or hate', spam: 'Spam or a scam',
  sexual: 'Sexual content', copyright: 'Copyright', other: 'Something else',
};
